import { stripHtml, truncateForMeta } from "@donoor/utils/string";
import type {
  PublicCampaign,
  PublicEvent,
  PublicPlan,
  PublicQRCode,
  PublicOrganization,
  PublicShop,
} from "~/composables/useRechercheData";

/**
 * Service de fetch des 6 collections publiques de /recherche.
 *
 * Fonctions pures (pas de réactivité Vue) : elles prennent l'URL de base + les
 * params et renvoient la page paginée. Consommées par les `useInfiniteQuery`
 * de useRechercheData (migration TanStack 2026-08-03). Isolées ici pour être
 * testables et réutilisables, et pour que le composable reste lisible.
 */

/**
 * Ce que contient TOUT le rayon, renvoyé par l'API avec chaque page : de quoi
 * construire la liste des filtres sans dépendre des 30 éléments chargés.
 */
export interface RechercheFacets {
  categories?: string[];
  /** Nombre de résultats par thème dans tout le rayon. */
  comptes?: Record<string, number>;
  /** Événements : types d'activité présents. */
  types?: string[];
  /** Adhésions : prix « à partir de » de chaque adhésion (tranches de prix). */
  prix?: number[];
  /** Événements : dates de début (raccourcis de période). */
  debuts?: string[];
}

/**
 * Filtres du rayon appliqués PAR L'API, avant pagination (2026-09-26).
 * Avant, le navigateur les appliquait à la seule page chargée : 3
 * associations sportives affichées sur 16 en production.
 */
export interface RechercheFiltresServeur {
  category?: string;
  /** Événements : type d'activité (`activity_type`). */
  type?: string;
  ville?: string;
  date_from?: string;
  date_to?: string;
  price_min?: number;
  price_max?: number;
}

export interface RecherchePage<T> {
  data: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  facets?: RechercheFacets;
}

interface RawPaginated<T> {
  success: boolean;
  data: T[];
  pagination?: {
    total?: number;
    limit?: number;
    offset?: number;
    hasMore?: boolean;
  };
  facets?: RechercheFacets;
}

export interface RechercheFetchParams {
  baseUrl: string;
  limit: number;
  offset: number;
  /** Terme de recherche serveur (single-type uniquement). undefined = absent. */
  search?: string;
  /** Filtres du rayon (single-type uniquement). */
  filtres?: RechercheFiltresServeur;
  signal?: AbortSignal;
}

// Construit la query en n'incluant `search` que s'il est défini (parité avec
// l'ancien comportement : pas de param search vide envoyé à l'API).
function buildQuery(p: RechercheFetchParams): Record<string, string | number> {
  const q: Record<string, string | number> = { limit: p.limit, offset: p.offset };
  if (p.search) q.search = p.search;
  for (const [cle, valeur] of Object.entries(p.filtres ?? {})) {
    if (valeur !== undefined && valeur !== null && valeur !== "") q[cle] = valeur;
  }
  return q;
}

// Normalise la pagination (l'API renvoie toujours pagination, mais on blinde
// les valeurs manquantes pour ne jamais casser getNextPageParam).
function normalize<T>(raw: RawPaginated<T>, params: RechercheFetchParams): RecherchePage<T> {
  const data = raw?.data || [];
  const total = raw?.pagination?.total ?? data.length;
  const offset = raw?.pagination?.offset ?? params.offset;
  const limit = raw?.pagination?.limit ?? params.limit;
  const hasMore = raw?.pagination?.hasMore ?? offset + data.length < total;
  return { data, pagination: { total, limit, offset, hasMore }, facets: raw?.facets };
}

export async function fetchCampaignsPage(
  params: RechercheFetchParams,
): Promise<RecherchePage<PublicCampaign>> {
  const raw = await $fetch<RawPaginated<PublicCampaign>>(
    `${params.baseUrl}/api/campaigns/public`,
    { query: buildQuery(params), signal: params.signal },
  );
  const page = normalize(raw, params);
  // Parité avec l'ancien transform (L231-239) : tronque la description pour
  // alléger le payload SSR (les cartes n'affichent qu'un extrait).
  page.data = page.data.map((c) => ({
    ...c,
    description: c.description
      ? truncateForMeta(stripHtml(c.description), 160)
      : c.description,
  }));
  return page;
}

export async function fetchEventsPage(
  params: RechercheFetchParams,
): Promise<RecherchePage<PublicEvent>> {
  const raw = await $fetch<RawPaginated<PublicEvent>>(
    `${params.baseUrl}/api/organizations/public/all-events`,
    { query: buildQuery(params), signal: params.signal },
  );
  return normalize(raw, params);
}

export async function fetchAdhesionsPage(
  params: RechercheFetchParams,
): Promise<RecherchePage<PublicPlan>> {
  const raw = await $fetch<RawPaginated<PublicPlan>>(
    `${params.baseUrl}/api/organizations/public/all-membership-plans`,
    { query: buildQuery(params), signal: params.signal },
  );
  return normalize(raw, params);
}

export async function fetchShopsPage(
  params: RechercheFetchParams,
): Promise<RecherchePage<PublicShop>> {
  const raw = await $fetch<RawPaginated<PublicShop>>(
    `${params.baseUrl}/api/public/shops`,
    { query: buildQuery(params), signal: params.signal },
  );
  return normalize(raw, params);
}

export async function fetchQrCodesPage(
  params: RechercheFetchParams,
): Promise<RecherchePage<PublicQRCode>> {
  const raw = await $fetch<RawPaginated<PublicQRCode>>(
    `${params.baseUrl}/api/qr-codes/public`,
    { query: buildQuery(params), signal: params.signal },
  );
  return normalize(raw, params);
}

export async function fetchOrganizationsPage(
  params: RechercheFetchParams,
): Promise<RecherchePage<PublicOrganization>> {
  const raw = await $fetch<RawPaginated<PublicOrganization>>(
    `${params.baseUrl}/api/organizations/public`,
    { query: buildQuery(params), signal: params.signal },
  );
  return normalize(raw, params);
}

/** Rayons dont le panneau de recherche montre les thèmes. */
export type RayonThemes = "associations" | "cagnottes" | "evenements" | "adhesions" | "boutiques";

/**
 * Nombre de résultats par thème dans un rayon (vignettes du panneau de
 * recherche). Une seule ligne demandée : seuls les compteurs servent.
 */
export async function fetchComptesThemes(params: {
  baseUrl: string;
  rayon: RayonThemes;
  signal?: AbortSignal;
}): Promise<Record<string, number>> {
  const p = { baseUrl: params.baseUrl, limit: 1, offset: 0, signal: params.signal };
  const fetchers = {
    associations: fetchOrganizationsPage,
    cagnottes: fetchCampaignsPage,
    evenements: fetchEventsPage,
    adhesions: fetchAdhesionsPage,
    boutiques: fetchShopsPage,
  } satisfies Record<RayonThemes, (p: RechercheFetchParams) => Promise<RecherchePage<unknown>>>;
  const page = await fetchers[params.rayon](p);
  return page.facets?.comptes ?? {};
}
